import { useEffect, useState } from 'react'
import { useShopify } from './useShopify'

interface UseDesignModeVisibilityOptions {
  sectionId?: string
}

/**
 * Controls visibility of overlays in the Shopify theme editor (design mode).
 *
 * - With `sectionId`: hidden by default, shown when the merchant selects
 *   that section in the editor sidebar, hidden again on deselect.
 * - Without `sectionId`: always hidden in design mode (non-editable overlays).
 * - Outside design mode: always visible.
 */
export const useDesignModeVisibility = (
  options?: UseDesignModeVisibilityOptions,
) => {
  const { designMode: isDesignMode } = useShopify()
  const sectionId = options?.sectionId

  const [isSectionSelected, setIsSectionSelected] = useState(false)

  useEffect(() => {
    if (!isDesignMode || !sectionId) return

    const handleSectionSelect = (event: Event) => {
      const customEvent = event as CustomEvent<{ sectionId?: string }>
      if (customEvent.detail?.sectionId === sectionId) {
        setIsSectionSelected(true)
      }
    }

    const handleSectionDeselect = (event: Event) => {
      const customEvent = event as CustomEvent<{ sectionId?: string }>
      if (customEvent.detail?.sectionId === sectionId) {
        setIsSectionSelected(false)
      }
    }

    document.addEventListener('shopify:section:select', handleSectionSelect)
    document.addEventListener('shopify:section:deselect', handleSectionDeselect)

    return () => {
      document.removeEventListener(
        'shopify:section:select',
        handleSectionSelect,
      )
      document.removeEventListener(
        'shopify:section:deselect',
        handleSectionDeselect,
      )
    }
  }, [isDesignMode, sectionId])

  const isVisible = !isDesignMode || (sectionId ? isSectionSelected : false)

  return { isDesignMode, isVisible }
}
